// ---------------------------------------------------------------------------
// useConversation.ts
//
// Owns the full lifecycle of a Conversation in Firestore.
//
// Auth:     useGetIdentity() from react-admin — calls authProvider.getIdentity()
//           which returns the cached Firestore user (id, first_name, etc.)
// Firestore: getFirestore() directly from firebase/firestore, same pattern
//           as authProvider.ts — no custom `db` import needed.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';
import { useGetIdentity } from 'react-admin';
import {
    getFirestore,
    doc,
    collection,
    addDoc,
    setDoc,
    updateDoc,
    onSnapshot,
    query,
    orderBy,
    serverTimestamp,
    Timestamp,
} from 'firebase/firestore';
import type { ConversationMeta, ConversationTurn } from './aiSearchTypes';
import type { ContextRef } from './contextTypes';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert a Firestore Timestamp or plain string to an ISO string. */
function toIso(val: Timestamp | string | undefined | null): string {
    if (!val) return new Date().toISOString();
    if (val instanceof Timestamp) return val.toDate().toISOString();
    return val;
}

/** Extract plain text from a turn's blocks for the Firestore `text` field. */
function extractText(turn: ConversationTurn): string {
    if (turn.text) return turn.text;
    return turn.blocks
        .filter(
            (b): b is { type: 'text'; content: string } => b.type === 'text'
        )
        .map(b => b.content)
        .join('\n\n');
}

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

export interface UseConversationReturn {
    /** Full conversation metadata from Firestore, or null before first submit on fresh path. */
    conversationMeta: ConversationMeta | null;
    /** All turns, ordered by index. Optimistic turns are appended immediately. */
    turns: ConversationTurn[];
    /** True while Firestore is loading an existing conversation on mount. */
    loading: boolean;

    /**
     * FRESH PATH ONLY. Creates the conversations/{id} document.
     * Call once, on first user submit, before saveUserTurn().
     */
    createConversation: (
        conversationId: string,
        firstQuery: string,
        contextRef: ContextRef | null
    ) => Promise<void>;

    /**
     * Writes a user turn to the turns/ sub-collection.
     * The caller must pass the index captured from turns.length BEFORE
     * appending any optimistic turns, to prevent collisions with the
     * previous assistant turn's index.
     */
    saveUserTurn: (
        conversationId: string,
        text: string,
        mentionRefs: ContextRef[],
        blocks: ConversationTurn['blocks'],
        index: number
    ) => Promise<number>;

    /**
     * Writes an assistant turn to the turns/ sub-collection and bumps
     * conversations/{id}.updatedAt.
     */
    saveAssistantTurn: (
        conversationId: string,
        userTurnIndex: number,
        turn: ConversationTurn
    ) => Promise<void>;

    /**
     * Patches the conversation title. Call after the first API response
     * returns a title suggestion.
     */
    updateTitle: (conversationId: string, title: string) => Promise<void>;

    /**
     * Appends an optimistic turn to local state immediately, before Firestore
     * confirms. The onSnapshot listener reconciles once the write lands.
     */
    appendOptimisticTurn: (turn: ConversationTurn) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param conversationId  The UUID from the URL param. Always present.
 * @param isFreshNav      True when location.state.contextRef is present —
 *                        suppresses the Firestore load-path onSnapshot.
 */
export function useConversation(
    conversationId: string,
    isFreshNav: boolean
): UseConversationReturn {
    // useGetIdentity calls authProvider.getIdentity() which returns the
    // cached Firestore user object — identity.id is the Firebase Auth UID.
    const { identity } = useGetIdentity();

    const [conversationMeta, setConversationMeta] =
        useState<ConversationMeta | null>(null);
    const [turns, setTurns] = useState<ConversationTurn[]>([]);
    const [loading, setLoading] = useState(!isFreshNav);

    // -----------------------------------------------------------------------
    // SAVED PATH — subscribe to Firestore on mount
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (isFreshNav) return; // fresh path: no read needed

        const db = getFirestore();
        setLoading(true);

        const metaUnsub = onSnapshot(
            doc(db, 'conversations', conversationId),
            snap => {
                if (!snap.exists()) return;
                const data = snap.data();
                setConversationMeta({
                    id: data.id,
                    userId: data.userId,
                    title: data.title ?? '',
                    createdAt: toIso(data.createdAt),
                    updatedAt: toIso(data.updatedAt),
                    contextRef: data.contextRef ?? null,
                });
            },
            err => {
                console.error('useConversation: meta onSnapshot error', err);
            }
        );

        const turnsUnsub = onSnapshot(
            query(
                collection(db, 'conversations', conversationId, 'turns'),
                orderBy('index')
            ),
            snap => {
                const loaded: ConversationTurn[] = snap.docs.map(d => {
                    const data = d.data();
                    return {
                        role: data.role,
                        blocks: data.blocks ?? [],
                        text: data.text ?? '',
                        mentionRefs: data.mentionRefs ?? [],
                        contextRefs: data.contextRefs ?? [],
                        createdAt: toIso(data.createdAt),
                        index: data.index,
                    } satisfies ConversationTurn;
                });
                setTurns(loaded);
                setLoading(false);
            },
            err => {
                console.error('useConversation: turns onSnapshot error', err);
                setLoading(false);
            }
        );

        return () => {
            metaUnsub();
            turnsUnsub();
        };
    }, [conversationId, isFreshNav]);

    // -----------------------------------------------------------------------
    // createConversation
    // -----------------------------------------------------------------------

    const createConversation = useCallback(
        async (
            convId: string,
            firstQuery: string,
            contextRef: ContextRef | null
        ) => {
            if (!identity?.id)
                throw new Error('useConversation: no authenticated user');

            const db = getFirestore();
            const raw = firstQuery.trim();
            const title = raw.slice(0, 60) + (raw.length > 60 ? '…' : '');

            await setDoc(doc(db, 'conversations', convId), {
                id: convId,
                userId: identity.id,
                title,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                contextRef: contextRef ?? null,
            });

            // Populate local meta immediately — onSnapshot will confirm
            setConversationMeta({
                id: convId,
                userId: String(identity.id),
                title,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                contextRef,
            });
        },
        [identity?.id]
    );

    // -----------------------------------------------------------------------
    // saveUserTurn
    // -----------------------------------------------------------------------

    const saveUserTurn = useCallback(
        async (
            convId: string,
            text: string,
            mentionRefs: ContextRef[],
            blocks: ConversationTurn['blocks'],
            index: number
        ): Promise<number> => {
            const db = getFirestore();

            await addDoc(collection(db, 'conversations', convId, 'turns'), {
                index,
                role: 'user',
                text,
                blocks,
                mentionRefs,
                contextRefs: [],
                createdAt: serverTimestamp(),
            });

            return index;
        },
        []
    );

    // -----------------------------------------------------------------------
    // saveAssistantTurn
    // -----------------------------------------------------------------------

    const saveAssistantTurn = useCallback(
        async (
            convId: string,
            userTurnIndex: number,
            turn: ConversationTurn
        ) => {
            const db = getFirestore();
            const index = userTurnIndex + 1;
            const text = extractText(turn);

            await addDoc(collection(db, 'conversations', convId, 'turns'), {
                index,
                role: 'assistant',
                text,
                blocks: turn.blocks,
                mentionRefs: [],
                contextRefs: turn.contextRefs ?? [],
                createdAt: serverTimestamp(),
            });

            await updateDoc(doc(db, 'conversations', convId), {
                updatedAt: serverTimestamp(),
            });
        },
        []
    );

    // -----------------------------------------------------------------------
    // updateTitle
    // -----------------------------------------------------------------------

    const updateTitle = useCallback(async (convId: string, title: string) => {
        const db = getFirestore();
        await updateDoc(doc(db, 'conversations', convId), { title });
        setConversationMeta(prev => (prev ? { ...prev, title } : prev));
    }, []);

    // -----------------------------------------------------------------------
    // appendOptimisticTurn
    // -----------------------------------------------------------------------

    const appendOptimisticTurn = useCallback((turn: ConversationTurn) => {
        setTurns(prev => [...prev, turn]);
    }, []);

    return {
        conversationMeta,
        turns,
        loading,
        createConversation,
        saveUserTurn,
        saveAssistantTurn,
        updateTitle,
        appendOptimisticTurn,
    };
}
